/* Story Trail — scroll-linked trail draw + per-stop reveal observers.
   Lightweight (~5KB). No deps. */
(function () {
  if (typeof window === 'undefined') return;

  function init(root) {
    if (!root || root.dataset.stInit === '1') return;
    root.dataset.stInit = '1';

    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    splitTagline(root);
    splitHeadlines(root);

    var hero = root.querySelector('.story-trail__hero');
    if (hero) {
      requestAnimationFrame(function () { root.classList.add('is-visible'); });
    }

    var rail = root.querySelector('.story-trail__rail');
    var line = root.querySelector('.story-trail__line');
    var progress = line && line.querySelector('.trail-progress');
    var base = line && line.querySelector('.trail-base');

    if (rail && line && progress && base) {
      drawTrail(root, rail, line, progress, base, prefersReduced);
    }

    revealStops(root, prefersReduced);
    revealOutro(root, prefersReduced);
    accentTrailOnStops(root);
  }

  function revealOutro(root, prefersReduced) {
    var outro = root.querySelector('.story-trail__outro');
    if (!outro) return;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      outro.classList.add('is-revealed');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-revealed');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.3 });
    io.observe(outro);
  }

  function splitTagline(root) {
    var tagline = root.querySelector('[data-st-tagline]');
    if (!tagline || tagline.dataset.split === '1') return;
    var text = tagline.textContent.trim();
    tagline.textContent = '';
    var words = text.split(/\s+/);
    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'word';
      span.textContent = word;
      tagline.appendChild(span);
      if (i < words.length - 1) {
        tagline.appendChild(document.createTextNode(' '));
      }
    });
    tagline.dataset.split = '1';
  }

  function splitHeadlines(root) {
    root.querySelectorAll('[data-st-headline]').forEach(function (h) {
      if (h.dataset.split === '1') return;
      var lines = (h.dataset.lines || h.textContent).split('||');
      h.innerHTML = '';
      lines.forEach(function (line) {
        var lineWrap = document.createElement('span');
        lineWrap.className = 'line';
        var inner = document.createElement('span');
        inner.innerHTML = line.trim();
        lineWrap.appendChild(inner);
        h.appendChild(lineWrap);
      });
      h.dataset.split = '1';
    });
  }

  // Build the SVG trail path: a clean vertical line with subtle bows at each stop.
  function drawTrail(root, rail, lineEl, progress, base, prefersReduced) {
    var stops = Array.from(rail.querySelectorAll('.story-trail__stop'));
    if (!stops.length) return;

    var svg = lineEl.querySelector('svg');
    var pennantGroup = lineEl.querySelector('.story-trail__pennants');
    var dotGroup = lineEl.querySelector('.story-trail__dots');
    var maskRect = lineEl.querySelector('.trail-reveal');
    var trailStyle = root.getAttribute('data-trail-style') || 'stitch';

    // Hoisted state — accessed by both build() and onScroll()
    var width = 0;
    var height = 0;
    var len = 0;

    function build() {
      var isMobile = window.matchMedia('(max-width: 1024px)').matches;
      var railRect = rail.getBoundingClientRect();
      width = lineEl.offsetWidth || 120;
      height = rail.offsetHeight;

      svg.setAttribute('viewBox', '0 0 ' + width + ' ' + height);
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.setAttribute('width', width);
      svg.setAttribute('height', height);

      var cx = width / 2;
      var anchors = [];

      stops.forEach(function (stop) {
        var stopRect = stop.getBoundingClientRect();
        var midY = (stopRect.top - railRect.top) + (stopRect.height / 2);
        var side = stop.getAttribute('data-side');
        anchors.push({ y: midY, side: side });
      });

      var d = 'M ' + cx + ' 0';

      if (isMobile) {
        // Mobile: dead-straight vertical rail along the left, no bows
        d += ' L ' + cx + ' ' + height;
      } else {
        var sway = Math.min(width * 0.18, 18);
        anchors.forEach(function (a, i) {
          var prevY = i === 0 ? 0 : anchors[i - 1].y;
          var midY = a.y;
          var bowX = cx + (a.side === 'left' ? -sway : sway);
          var enterY = prevY + (midY - prevY) * 0.45;
          d += ' C ' + cx + ' ' + (prevY + (midY - prevY) * 0.2) +
               ', ' + bowX + ' ' + enterY +
               ', ' + cx + ' ' + midY;
          d += ' L ' + cx + ' ' + Math.min(midY + 16, height);
        });
        d += ' L ' + cx + ' ' + height;
      }

      base.setAttribute('d', d);
      progress.setAttribute('d', d);

      len = progress.getTotalLength();

      // Initialize mask: at rest, only the very tip of the trail is filled (8px revealed).
      if (maskRect) {
        maskRect.setAttribute('height', prefersReduced ? String(height) : '8');
      }

      // Pennants distributed along the path
      if (trailStyle === 'pennant' && pennantGroup) {
        pennantGroup.innerHTML = '';
        var step = 42;
        for (var s = 18; s < len; s += step) {
          var pt = progress.getPointAtLength(s);
          var tri = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
          tri.setAttribute('class', 'pennant');
          var size = 8;
          tri.setAttribute('points',
            (pt.x - size) + ',' + pt.y + ' ' +
            (pt.x + size) + ',' + pt.y + ' ' +
            pt.x + ',' + (pt.y + size * 1.7));
          pennantGroup.appendChild(tri);
        }
      } else if (pennantGroup) {
        pennantGroup.innerHTML = '';
      }

      // Anchor dots at each stop centre
      if (dotGroup) {
        dotGroup.innerHTML = '';
        anchors.forEach(function (a, i) {
          var dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          dot.setAttribute('class', 'trail-dot');
          dot.setAttribute('cx', cx);
          dot.setAttribute('cy', a.y);
          dot.setAttribute('r', 7);
          dot.setAttribute('data-stop-index', i);
          dotGroup.appendChild(dot);
        });
      }

      return len;
    }

    build();

    if (prefersReduced) {
      progress.style.strokeDashoffset = 0;
      return;
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var rect = rail.getBoundingClientRect();
        var vh = window.innerHeight;
        var start = rect.top - vh * 0.85;
        var end = rect.bottom - vh * 0.4;
        var span = Math.max(end - start, 1);
        var pos = -start;
        var ratio = Math.max(0, Math.min(1, pos / span));
        if (maskRect) {
          // Always reveal at least 8px so the very tip is filled before any scroll
          var revealedHeight = Math.max(8, ratio * height);
          maskRect.setAttribute('height', String(revealedHeight));
        }
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Rebuild on resize / image load
    var rebuildTimer;
    function rebuild() {
      clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(function () {
        build();
        onScroll();
      }, 120);
    }

    window.addEventListener('resize', rebuild, { passive: true });
    rail.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', rebuild, { once: true });
    });
  }

  function revealStops(root, prefersReduced) {
    var stops = root.querySelectorAll('.story-trail__stop');
    var dots = root.querySelectorAll('.trail-dot');

    if (prefersReduced || !('IntersectionObserver' in window)) {
      stops.forEach(function (s) { s.classList.add('is-revealed'); });
      dots.forEach(function (d) { d.classList.add('is-active'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-revealed');
          var idx = Array.from(stops).indexOf(e.target);
          if (idx !== -1 && dots[idx]) dots[idx].classList.add('is-active');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -10% 0px' });

    stops.forEach(function (s) { io.observe(s); });
  }

  function accentTrailOnStops(root) {
    // Trail stays the theme's ink colour throughout. Accents only affect per-stop UI (eyebrow pill, number outline).
    return;
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    document.querySelectorAll('.story-trail').forEach(init);
  });

  // Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('.story-trail');
    if (root) init(root);
  });
})();
